import React from "react";
import {
  Divider,
  Grid,
  Paper,
  Typography,
  Box,
  Button
} from "@material-ui/core";
import { Schedule } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { LogoIconGGV } from "../Logo";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useConsumerAuth } from "../../hooks";
import { formatDate } from "../../utils";
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_SCHEDULED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CHECKED_IN
} from "../../constants";
import { useTranslation } from "react-i18next";

const useClasses = makeStyles(theme => ({
  scheduleIcon: {
    color: theme.palette.info.dark
  }
}));

const Covid19DoseCardWrapper = ({ children }) => {
  const classes = useClasses();

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid item>
          <Schedule fontSize="small" className={classes.scheduleIcon} />
        </Grid>
        <Grid item xs>
          {children}
        </Grid>
      </Grid>
    </Box>
  );
};

const Covid19DoseCard = ({
  dose,
  doses,
  date,
  address,
  locationName,
  status,
  appointmentId
}) => {
  const history = useHistory();
  const { dob } = useConsumerAuth();
  const { t } = useTranslation();

  return (
    <Box data-cy={`appointment-${appointmentId}`}>
      <Typography component="h4" variant="body2">
        <strong>
          {t("components.covid19_vaccine_card.dose", {
            dose: dose,
            doses: doses.length
          })}
        </strong>{" "}
        &mdash; {status}
      </Typography>
      <Typography variant="body2">
        {moment(date).format("dddd MMMM Do, YYYY")}
        {date.trim().length > 10 && ` | ${moment(date).format("h:mma")}`}
      </Typography>
      <Typography>{locationName}</Typography>
      <Typography>{address}</Typography>
      {(status === APPOINTMENT_STATUS_SCHEDULED ||
        status === APPOINTMENT_STATUS_CHECKED_IN) && (
        <Box mt={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              history.push({
                pathname: `/appointment/${appointmentId}/${formatDate(
                  dob,
                  "YYYY-MM-DD",
                  "YYYYMMDD"
                )}`
              })
            }
          >
            {t("components.covid19_vaccine_card.button_confirm")}
          </Button>
        </Box>
      )}
      {status === APPOINTMENT_STATUS_COMPLETED && (
        <Box mt={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              history.push({
                pathname: `/appointment/vax-certificate`
              })
            }
          >
            {t("components.covid19_vaccine_card.button_certificate")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const Covid19VaccineCard = ({ appointment }) => {
  const { t } = useTranslation();

  const formatAddress = obj => {
    let addressParts = [];
    const addressKeys = [
      { key: "loc_addr1", separator: ", " },
      { key: "loc_addr2", separator: ", " },
      { key: "loc_city", separator: ", " },
      { key: "loc_st" },
      { key: "loc_zip" }
    ];

    addressKeys.forEach(addressKey => {
      if (obj[addressKey.key])
        addressParts = [
          ...addressParts,
          `${obj[addressKey.key]}${addressKey.separator || " "}`
        ];
    });

    return addressParts;
  };

  return (
    <Paper>
      <Box p={2}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <LogoIconGGV height="20px" />
          </Grid>
          <Grid item xs>
            <Typography variant="h6" component="h3">
              {t("components.covid19_vaccine_card.title")}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      {appointment.doses && appointment.doses.length > 0 && (
        <>
          {appointment.doses.map((dose, index) => (
            <Covid19DoseCardWrapper key={`${dose.appointment_id}-${index}`}>
              <Covid19DoseCard
                appointmentId={dose.appointment_id}
                dose={index + 1}
                doses={appointment.doses}
                date={dose.scheduled_dt}
                locationName={dose.loc_name}
                address={formatAddress(dose)}
                status={
                  APPOINTMENT_STATUSES[dose.appointment_status] || "Unknown"
                }
              />
            </Covid19DoseCardWrapper>
          ))}
        </>
      )}
    </Paper>
  );
};

export { Covid19VaccineCard };
